import {
  guardEffectSchema,
  MAX_GAMES_PER_DAY,
  MAX_RULE_GUARDS,
  ruleGuardSchema,
  weekdaySchema,
  type RuleGuard,
} from '@gamble-chess/contracts';
import { useState, type JSX } from 'react';

import { describeGuard, guardEffectLabels, guardKindLabels, weekdayLabels } from '../format.ts';
import {
  initialRuleGuardDraft,
  parseRuleGuard,
  type GuardKind,
  type RuleGuardDraft,
} from '../ruleGuardDraft.ts';
import { Alert } from '../ui/Alert.tsx';
import { Field } from '../ui/Field.tsx';
import {
  fieldGridClass,
  hintClass,
  inputClass,
  labelClass,
  legendClass,
  secondaryButtonClass,
} from '../ui/styles.ts';

const guardKinds: readonly GuardKind[] = ruleGuardSchema.options.map(
  (option) => option.shape.kind.value,
);

export interface RuleGuardFieldsProps {
  readonly value: readonly RuleGuard[];
  readonly onChange: (guards: readonly RuleGuard[]) => void;
  /** The zone the days and times are read in, so the form says whose clock it means. */
  readonly timeZone: string;
}

/**
 * Builds the list of conditions a rule counts under: pick a kind, fill it in, say what it
 * does, and add it. A rule with no guards counts every game its filter matches.
 */
export function RuleGuardFields({ value, onChange, timeZone }: RuleGuardFieldsProps): JSX.Element {
  const [draft, setDraft] = useState<RuleGuardDraft>(initialRuleGuardDraft);
  const [invalid, setInvalid] = useState<string | undefined>(undefined);

  function add(): void {
    const parsed = parseRuleGuard(draft);
    if (!parsed.ok) {
      setInvalid(parsed.error);
      return;
    }
    setInvalid(undefined);
    onChange([...value, parsed.guard]);
  }

  return (
    <fieldset className="flex flex-col gap-3 rounded-lg border border-border bg-surface-sunken p-3">
      <legend className={`${legendClass} px-1`}>When it counts</legend>
      {value.length === 0 ? (
        <p className={hintClass}>
          Every game the filter matches counts. Add a condition to narrow that down.
        </p>
      ) : (
        <ul aria-label="Conditions on this rule" className="flex flex-col gap-2 text-sm">
          {value.map((guard, index) => (
            <li
              key={`${String(index)} ${describeGuard(guard)}`}
              className="flex flex-wrap items-center gap-2"
            >
              <span className="font-medium">{describeGuard(guard)}</span>
              <button
                type="button"
                onClick={() => {
                  onChange(value.filter((_, other) => other !== index));
                }}
                aria-label={`Remove condition ${describeGuard(guard)}`}
                className={secondaryButtonClass}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {value.length >= MAX_RULE_GUARDS ? null : (
        <>
          <div className={fieldGridClass}>
            <Field label="Condition">
              {(id) => (
                <select
                  id={id}
                  value={draft.kind}
                  onChange={(event) => {
                    const kind = guardKinds.find((option) => option === event.target.value);
                    if (kind !== undefined) {
                      setDraft({ ...draft, kind });
                    }
                  }}
                  className={inputClass}
                >
                  {guardKinds.map((kind) => (
                    <option key={kind} value={kind}>
                      {guardKindLabels[kind]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            {draft.kind === 'weekly-window' ? (
              <>
                <Field label="From">
                  {(id) => (
                    <input
                      id={id}
                      type="time"
                      value={draft.start}
                      onChange={(event) => {
                        setDraft({ ...draft, start: event.target.value });
                      }}
                      className={inputClass}
                    />
                  )}
                </Field>
                <Field label="To">
                  {(id) => (
                    <input
                      id={id}
                      type="time"
                      value={draft.end}
                      onChange={(event) => {
                        setDraft({ ...draft, end: event.target.value });
                      }}
                      className={inputClass}
                    />
                  )}
                </Field>
              </>
            ) : null}
            {draft.kind === 'date-range' ? (
              <>
                <Field label="First day">
                  {(id) => (
                    <input
                      id={id}
                      type="date"
                      value={draft.from}
                      onChange={(event) => {
                        setDraft({ ...draft, from: event.target.value });
                      }}
                      className={inputClass}
                    />
                  )}
                </Field>
                <Field label="Last day">
                  {(id) => (
                    <input
                      id={id}
                      type="date"
                      value={draft.to}
                      onChange={(event) => {
                        setDraft({ ...draft, to: event.target.value });
                      }}
                      className={inputClass}
                    />
                  )}
                </Field>
              </>
            ) : null}
            {draft.kind === 'daily-cap' ? (
              <Field label="Games a day">
                {(id) => (
                  <input
                    id={id}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={MAX_GAMES_PER_DAY}
                    step={1}
                    value={draft.gamesPerDay}
                    onChange={(event) => {
                      setDraft({ ...draft, gamesPerDay: event.target.value });
                    }}
                    className={inputClass}
                  />
                )}
              </Field>
            ) : null}
            <Field label="Then it">
              {(id) => (
                <select
                  id={id}
                  value={draft.effect}
                  onChange={(event) => {
                    const parsed = guardEffectSchema.safeParse(event.target.value);
                    if (parsed.success) {
                      setDraft({ ...draft, effect: parsed.data });
                    }
                  }}
                  className={inputClass}
                >
                  {guardEffectSchema.options.map((effect) => (
                    <option key={effect} value={effect}>
                      {guardEffectLabels[effect]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
          {draft.kind === 'weekly-window' ? (
            <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <legend className={labelClass}>Days</legend>
              {weekdaySchema.options.map((day) => (
                <label key={day} className="flex min-h-11 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={draft.days.includes(day)}
                    onChange={(event) => {
                      setDraft({
                        ...draft,
                        days: event.target.checked
                          ? [...draft.days, day]
                          : draft.days.filter((chosen) => chosen !== day),
                      });
                    }}
                    className="size-4"
                  />
                  {weekdayLabels[day]}
                </label>
              ))}
            </fieldset>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={add} className={secondaryButtonClass}>
              Add condition
            </button>
            <p className={hintClass}>
              Days and times are read in {timeZone}. A cap counts the games this rule already
              charged earlier the same day.
            </p>
          </div>
        </>
      )}
      {invalid === undefined ? null : <Alert>{invalid}</Alert>}
    </fieldset>
  );
}
